import { useEffect, useState, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useIsMobile } from '@/hooks/useIsMobile';
import { colors } from '@/theme';

// Helper to format time remaining
const formatTimeRemaining = (seconds) => {
  if (seconds <= 0) return 'Expired';

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  if (minutes > 0) {
    return `${minutes}m ${secs}s`;
  }
  return `${secs}s`;
};

const ExpirationTimer = ({
  expirationTime,
  onExpire,
  compact = false,
  showUrgency = true,
}) => {
  const isMobile = useIsMobile(520);

  const [secondsRemaining, setSecondsRemaining] = useState(() => {
    const now = Date.now();
    return Math.max(0, Math.floor((expirationTime - now) / 1000));
  });

  const isUrgent = useMemo(
    () => showUrgency && secondsRemaining > 0 && secondsRemaining < 86400,
    [secondsRemaining, showUrgency]
  );

  const isExpired = secondsRemaining <= 0;

  useEffect(() => {
    if (isExpired) {
      if (onExpire) onExpire();
      return;
    }

    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expirationTime - now) / 1000));
      setSecondsRemaining(remaining);

      if (remaining <= 0 && onExpire) {
        onExpire();
      }
    };

    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [expirationTime, onExpire, isExpired]);

  const displayText = formatTimeRemaining(secondsRemaining);

  const textColor = isExpired
    ? colors.error.main
    : isUrgent
      ? colors.warning.main
      : colors.grey[800];

  const fontSize = compact
    ? isMobile
      ? '0.875rem'
      : '1rem'
    : isMobile
      ? '1.5rem'
      : '2rem';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        variant={compact ? 'body1' : 'h4'}
        sx={{
          color: textColor,
          fontSize,
          fontWeight: isUrgent || isExpired ? 600 : 400,
        }}
      >
        {displayText}
      </Typography>
    </Box>
  );
};

export default ExpirationTimer;
